/**
 * TokenResolver.ts — Dynamic Token Engine
 *
 * Resolves Mustache-style {{path.to.value}} tokens inside block `content`
 * objects against a data context (typically the SSR product/category object).
 *
 * Design goals:
 *  1. Never throw — invalid paths silently resolve to '' or the original token.
 *  2. Deep-walk content objects, arrays, and nested structures recursively.
 *  3. Framework-agnostic: works identically on server (SSR) and client.
 *
 * Usage:
 *   import { resolveTokens } from '@agentic/magento-client/lib/editor/TokenResolver';
 *   const resolved = resolveTokens(block.content, context);
 */

// Token pattern
// Matches:  {{product.name}}  {{product.price_range.minimum_price.final_price.value}}
// Allows optional whitespace:  {{ product.name }}
const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

// Deep Getter
/**
 * Safely retrieves a deeply nested value from `obj` using a dot-delimited path.
 *
 * @example
 *   deepGet({ a: { b: { c: 42 } } }, 'a.b.c') → 42
 *   deepGet({}, 'x.y.z')                       → undefined
 */
export function deepGet(obj: unknown, path: string): unknown {
    if (obj == null || typeof path !== 'string' || path === '') return undefined;

    return path.split('.').reduce<unknown>((acc, key) => {
        if (acc == null) return undefined;
        if (typeof acc === 'object') return (acc as Record<string, unknown>)[key];
        return undefined;
    }, obj);
}

// String-level token resolution
/**
 * Replaces all `{{path}}` tokens inside a single string.
 *
 * Resolution rules:
 *  - Resolved value is a primitive → coerce to string and substitute in-place.
 *  - Resolved value is an object/array AND the string is exactly `{{token}}`
 *    (i.e. the entire string is one token) → return the raw value so that
 *    downstream components can consume structured data.
 *  - Path not found or undefined → replace with '' (empty string).
 *
 * @returns string or, in the "full-string token" edge case, the raw resolved value.
 */
export function resolveStringTokens(str: string, data: unknown): unknown {
    if (typeof str !== 'string') return str;

    // Quick exit — no tokens present
    if (!str.includes('{{')) return str;

    // Edge case: the whole string IS a single token → return raw value to keep
    // structured data (objects, numbers) intact instead of stringifying them.
    const fullMatch = str.match(/^\{\{\s*([\w.]+)\s*\}\}$/);
    if (fullMatch) {
        const val = deepGet(data, fullMatch[1]);
        return val !== undefined ? val : '';
    }

    // General case: one or more tokens embedded in surrounding text.
    return str.replace(TOKEN_RE, (_match, path: string) => {
        const val = deepGet(data, path);
        if (val === undefined || val === null) return '';
        if (typeof val === 'object') {
            try { return JSON.stringify(val); } catch { return ''; }
        }
        if (typeof val === 'string') return val;
        if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint') return String(val);
        return '';
    });
}

// Recursive content resolver
/**
 * Walks an arbitrary `content` value (string, array, or object) and resolves
 * every {{token}} found against `data`.
 *
 * @param content - The block's `content` field (from the layout JSON).
 * @param data    - The data context to resolve against.  Typically the entire
 *                  `context` object so tokens like `product.name` work.
 * @returns A structurally identical copy of `content` with all tokens resolved.
 *          The original `content` reference is never mutated.
 */
export function resolveTokens(content: unknown, data: unknown): unknown {
    // Null / undefined → pass through
    if (content == null) return content;

    // String → resolve tokens
    if (typeof content === 'string') {
        return resolveStringTokens(content, data);
    }

    // Array → map each element
    if (Array.isArray(content)) {
        return content.map((item) => resolveTokens(item, data));
    }

    // Plain object → shallow-clone and resolve each value
    if (typeof content === 'object') {
        const resolved: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(content)) {
            resolved[key] = resolveTokens(value, data);
        }
        return resolved;
    }

    // Primitives (number, boolean) → pass through
    return content;
}

// Live Preview Resolver
/**
 * Resolves tokens against the current Live Preview context.
 * Import `previewStore` from canvasStore to check the mode.
 *
 * Usage:
 *   const resolved = resolveLivePreview(block.content, previewContext, previewMode);
 */
export function resolveLivePreview(
    content: unknown,
    liveContext: Record<string, unknown> | null,
    mode: 'token' | 'live'
): unknown {
    // In "token" mode, return content as-is (with {{placeholders}} visible)
    if (mode === 'token' || !liveContext) {
        return content;
    }

    // In "live" mode, resolve all tokens against real Magento data
    return resolveTokens(content, liveContext);
}

/**
 * Helper: given a content string and preview state, returns the display value.
 * Convenience for single-string resolution (e.g. ProductTitle block).
 */
export function resolvePreviewString(
    str: string,
    liveContext: Record<string, unknown> | null,
    mode: 'token' | 'live'
): string {
    if (mode === 'token' || !liveContext) return str;
    const resolved = resolveStringTokens(str, liveContext);
    if (typeof resolved === 'string') return resolved;
    if (typeof resolved === 'number' || typeof resolved === 'boolean') return String(resolved);
    return '';
}

export default resolveTokens;
